import { toast } from "sonner";

import { workspace } from "@/components/workspace/controller";
import { keys } from "@/hooks/queries";
import { api, queryClient, type NodeOut, type SearchHit } from "@/lib/api";
import { useGraph } from "@/stores/graph";
import { useTabs } from "@/stores/tabs";

/** Following a `[[wikilink]]`. One implementation for BOTH surfaces — the Live
 *  editor's decoration layer and the read-only Preview's anchor renderer — so a
 *  link cannot behave one way in one mode and another way in the other. */

/** Open a resolved node on the surface that can actually EDIT it.
 *
 *  A note used to fall into the atlas branch: clicking `[[My Note]]` switched to
 *  the graph and highlighted a dot, which is not "opening" the note by any
 *  reading. Only a concept — which has no document of its own — belongs in the
 *  atlas. */
function openNode(node: NodeOut, { background = false } = {}): void {
  const tabs = useTabs.getState();
  if (node.type === "document" && node.document_id) {
    if (background) {
      workspace.openDoc(
        { id: node.document_id, title: node.label, docKind: "document" },
        { background },
      );
    } else {
      tabs.openReader(node.document_id, node.label);
    }
    return;
  }
  if (node.type === "concept") {
    // A concept has no document, so there is nothing to open in a tab — it is a
    // place in the atlas. ⌘/Ctrl-click therefore behaves like a plain click
    // rather than pretending to open something.
    tabs.activate("graph");
    useGraph.getState().requestFocus(node.id);
    return;
  }
  // Notes, org hubs, AI outputs — everything markdown-backed opens in the editor.
  if (background) workspace.openMarkdown(node.id, node.label, { background });
  else tabs.openMarkdown(node.id, node.label);
}

/** Create the missing note and open it, the "unresolved link is an invitation"
 *  behaviour Obsidian has. Only ever runs from an explicit toast action. */
async function createAndOpen(label: string): Promise<void> {
  try {
    const node = (await api.createNode({
      type: "note",
      label,
      text: `# ${label}\n\n`,
    } as never)) as unknown as NodeOut;
    if (typeof node?.id !== "string" || !node.id) throw new Error("unexpected response");
    await queryClient.invalidateQueries({ queryKey: keys.overview });
    await queryClient.invalidateQueries({ queryKey: keys.vault });
    useTabs.getState().openMarkdown(node.id, node.label || label);
  } catch (e) {
    toast.error("Could not create that note", { description: String(e) });
  }
}

/** Resolve a `[[label]]` and go there.
 *
 *  Resolution is a ladder, because an exact hit is not the only thing a writer
 *  means. `/api/nodes/resolve` is an exact, case-insensitive match; when it
 *  misses (a typo, a plural, a half-remembered title) the search index gets a
 *  turn before we give up, and a hit whose label matches ignoring case and
 *  punctuation is taken as the intended target. Only when BOTH miss is the link
 *  reported unresolved — and then with a way to create it, rather than a dead end. */
export async function openWikiLink(rawLabel: string): Promise<void> {
  const label = rawLabel.trim();
  if (!label) return;
  try {
    const exact = await api
      .request<NodeOut | null>("GET", `/api/nodes/resolve?label=${encodeURIComponent(label)}`)
      .catch(() => null);
    if (exact?.id) {
      openNode(exact);
      return;
    }
    const res = await api
      .request<{ query: string; hits: SearchHit[] }>(
        "GET",
        `/api/search?q=${encodeURIComponent(label)}&limit=5`,
      )
      .catch(() => null);
    const hits = res?.hits ?? [];
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "");
    const near = hits.find((h) => norm(h.node.label) === norm(label));
    if (near) {
      openNode(near.node);
      return;
    }
    toast(`No note named “${label}” yet`, {
      action: { label: "Create it", onClick: () => void createAndOpen(label) },
    });
  } catch {
    toast.error("Could not open that link");
  }
}

/** Resolve a `[[label]]` to the node it means, or null. The shared ladder above,
 *  factored out so a caller that wants the TARGET rather than the navigation
 *  (opening it in a browser tab, say) doesn't have to re-implement resolution. */
export async function resolveWikiLink(rawLabel: string): Promise<NodeOut | null> {
  const label = rawLabel.trim();
  if (!label) return null;
  const exact = await api
    .request<NodeOut | null>("GET", `/api/nodes/resolve?label=${encodeURIComponent(label)}`)
    .catch(() => null);
  if (exact?.id) return exact;
  const res = await api
    .request<{ query: string; hits: SearchHit[] }>(
      "GET",
      `/api/search?q=${encodeURIComponent(label)}&limit=5`,
    )
    .catch(() => null);
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "");
  return res?.hits?.find((h) => norm(h.node.label) === norm(label))?.node ?? null;
}

/**
 * Follow a `[[wikilink]]` into a BACKGROUND TAB (⌘/Ctrl-click).
 *
 * "New tab" means a tab in THIS app's dock, not a second copy of the whole
 * studio in a browser tab — a browser tab would boot the app again, lose the
 * editor's unsaved buffer, and leave two workspaces that know nothing about
 * each other. The panel opens without taking focus, which is the half of
 * ⌘/Ctrl-click that matters: you keep reading where you are.
 */
export async function openWikiLinkInBackground(rawLabel: string): Promise<void> {
  const node = await resolveWikiLink(rawLabel);
  if (!node) {
    toast(`No note named “${rawLabel.trim()}” yet`);
    return;
  }
  openNode(node, { background: true });
}
